/**
 * Screen-time countdown math. A session reserves its whole length from the kid's saved time when it
 * starts; stopping early gives back the whole minutes still on the clock. The server owns the clock
 * (`startedAt` / `endsAt`), so closing the app or switching devices can't add time.
 */

const MINUTE = 60_000;

/** Warnings before time runs out, in minutes, largest first. */
export const WARNING_MINUTES = [5, 1] as const;
export type WarningMinutes = (typeof WARNING_MINUTES)[number];

export interface SessionClock {
  startedAt: number;
  endsAt: number;
  /** The session's length in whole minutes (what was reserved from the bank). */
  minutes: number;
}

/** Milliseconds left on the clock, never negative. */
export function remainingMs(s: Pick<SessionClock, "endsAt">, now: number): number {
  return Math.max(0, s.endsAt - now);
}

/** Whole minutes left, rounded down: 12 min 59 s shows as 12. */
export function remainingMinutes(s: Pick<SessionClock, "endsAt">, now: number): number {
  return Math.floor(remainingMs(s, now) / MINUTE);
}

/**
 * How long a session may run right now, in whole minutes (rounded down): the saved balance,
 * what's left of the weekly and daily caps, and the minutes until the schedule's end time.
 */
export function plannedMinutes(limits: {
  balance: number;
  weeklyRemaining?: number | null;
  dailyRemaining?: number | null;
  untilScheduleEnd?: number | null;
}): number {
  const caps = [limits.balance, limits.weeklyRemaining, limits.dailyRemaining, limits.untilScheduleEnd].filter(
    (v): v is number => typeof v === "number" && Number.isFinite(v),
  );
  return Math.max(0, Math.floor(Math.min(...caps)));
}

/**
 * The warning that is due now, if any. A warning only fires for sessions longer than it (a
 * 3-minute session gets the 1-minute warning but not the 5-minute one), and never once time is up.
 * `sent` lists warnings already delivered; the smallest due warning wins, so a late check that
 * finds 40 s left sends the 1-minute warning rather than a stale 5-minute one.
 */
export function dueWarning(s: SessionClock, now: number, sent: readonly WarningMinutes[] = []): WarningMinutes | null {
  const left = remainingMs(s, now);
  if (left <= 0) return null;
  let due: WarningMinutes | null = null;
  for (const w of WARNING_MINUTES) {
    if (s.minutes > w && left <= w * MINUTE) due = w;
  }
  if (due === null || sent.some((w) => w <= due!)) return null;
  return due;
}

/**
 * Settle a session stopped at `now`: minutes used are the elapsed time rounded up, and the
 * refund is the whole minutes still showing on the clock, so a kid keeps exactly what they saw.
 * Neither is ever negative, and used + refunded always equals the reserved minutes.
 */
export function settle(s: SessionClock, now: number): { usedMinutes: number; refundMinutes: number } {
  const refundMinutes = Math.min(s.minutes, remainingMinutes(s, now));
  return { usedMinutes: s.minutes - refundMinutes, refundMinutes };
}
